import { Component, Input, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { AiAgentResponse } from '../../../models/responses/ai-agent-response';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { KnowledgeBaseService } from '../../../services/knowledge-base.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDivider } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { KnowledgeDocumentResponse } from '../../../models/responses/knowledge-document-response';
import { ServiceResponse } from '../../../models/responses/service-response';
import { ServiceService } from '../../../services/service.service';
import { MatDialog } from '@angular/material/dialog';
import { CONFIRMATION_DIALOG_TEXT, ConfirmationDialogComponent } from '../../general-dialogs-components/confirmation-dialog/confirmation-dialog.component';
import { KnowledgeBaseStatus } from '../../../models/enums/knowledge-base-status';

@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    NgFor,
    MatIconModule,
    MatSelectModule,
    NgIf,
    MatDivider,
    MatProgressSpinner
  ],
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.scss'
})
export class KnowledgeBaseComponent {
  @Input() aiAgent!: AiAgentResponse;
  knowledgeBaseTypes: string[] = ['Website', 'PDF Document'];
  knowledgeName: FormControl = new FormControl("", Validators.required);
  webScrapeUrl: FormControl = new FormControl("", [Validators.required, Validators.pattern('^((https|ftp|smtp):\/\/)(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$')]);
  display: FormControl = new FormControl("", Validators.required);
  serviceForm!: FormGroup;
  isKnowledgeLoading: boolean = false;
  isDeleteKnowledgeLoading: boolean = false;
  isKnowledgeListLoading: boolean[] = [];
  isServicesLoading: boolean = false;
  file_store!: FileList;
  file_list: Array<string> = [];
  intervalId: any = '';
  knowledgeBaseType: FormControl = new FormControl(this.knowledgeBaseTypes[0]);

  constructor(
    private knowledgeService: KnowledgeBaseService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private serviceService: ServiceService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.initKnowledgeBasePolling();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiAgent'].currentValue) {
      this.initFormGroup();
      this.initServicesForm();
    }
  }

  initFormGroup() {
    this.serviceForm = this.formBuilder.group({
      services: this.formBuilder.array([])
    });
  }

  initServicesForm() {
    if (this.aiAgent && this.aiAgent.services) {
      this.services.clear();
      this.aiAgent.services.forEach(service => {
        this.services.push(this.formBuilder.group({
          id: [service.id],
          name: [service.name, Validators.required],
          price: [service.price, Validators.required],
          description: [service.description, Validators.required],
          businessId: [this.aiAgent.id, Validators.required]
        }));
      });
    }
  }

  get services(): FormArray<FormGroup> {
    return this.serviceForm.get('services') as FormArray;
  }

  addService() {
    this.services.push(this.formBuilder.group({
      id: [''],
      name: ['', Validators.required],
      price: ['', Validators.required],
      description: ['', Validators.required],
      businessId: [this.aiAgent.id, Validators.required]
    }));
  }

  getAllServices() {
    const services: ServiceResponse[] = [];

    for (let index = 0; index < this.services.length; index++) {
      const service = this.services.controls[index].value;
      const serviceId: number = parseInt(service['id']);

      services.push({
        id: isNaN(serviceId) ? undefined : serviceId,
        name: service['name'],
        price: service['price'],
        description: service['description'],
        aiAgentId: parseInt(service['businessId'])
      });
    }

    return services;
  }

  onServicesSubmit() {
    if (this.services.valid) {
      const services: ServiceResponse[] = this.getAllServices();

      const servicesToCreate: ServiceResponse[] = services.filter(service => service.id == undefined);
      const servicesToUpdate: ServiceResponse[] = services.filter(service => service.id != undefined);

      this.isServicesLoading = true;

      if (servicesToCreate.length > 0) {
        this.serviceService.createServices(servicesToCreate).subscribe({
          next: (services) => {
            if (servicesToUpdate.length == 0) {
              this.aiAgent.services = services;
              this.isServicesLoading = false;
              this.openSnackBar('Services saved', 'close');
              this.initServicesForm();
            }
          },
          error: () => {
            this.isServicesLoading = false;
            alert('Server error contact support');
          }
        });
      }

      if (servicesToUpdate.length > 0) {
        this.serviceService.updateServices(servicesToUpdate).subscribe({
          next: (services) => {
            this.aiAgent.services = services;
            this.isServicesLoading = false;
            this.openSnackBar('Services saved', 'close');
            this.initServicesForm();
          },
          error: () => {
            this.isServicesLoading = false;
            alert('Server error contact support');
          }
        });
      }
    }
  }

  onRemoveService(index: number) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Delete Service', data: 'Are you sure you want to delete this service?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        const id: number = this.services.at(index).value['id'];
        this.services.removeAt(index);

        if (!isNaN(id) && id) {
          this.isServicesLoading = true;

          this.serviceService.removeService(id).subscribe({
            next: (services) => {
              this.aiAgent.services = services;
              this.isServicesLoading = false;
              this.openSnackBar('Service deleted successfully', 'Close');
            },
            error: () => {
              this.isServicesLoading = false;
              alert('Server error contact support');
            }
          });
        }
      }
    })
  }

  // KNOWLEDGE BASE SERVICES

  handleFileInputChange(l: FileList): void {
    this.file_store = l;
    if (l.length) {
      const f = l[0];
      const count = l.length > 1 ? `(+${l.length - 1} files)` : "";
      this.display.patchValue(`${f.name}${count}`);
    } else {
      this.display.patchValue("");
    }
  }

  onFileSubmit(): void {
    if (this.display.valid && this.knowledgeName.valid) {
      this.isKnowledgeLoading = true;
      var fd = new FormData();
      this.file_list = [];

      for (let i = 0; i < this.file_store.length; i++) {
        fd.append("file", this.file_store[i], this.file_store[i].name);
        this.file_list.push(this.file_store[i].name);
      }

      this.knowledgeService.uploadKnowledgePdf(this.aiAgent.id, fd, this.knowledgeName.value).subscribe({
        next: () => {
        },
        error: () => {
          alert('There was an error, contact support');
        }
      });

      this.openSnackBar('File was sent successfully', 'Close');
      this.isKnowledgeLoading = false;
      this.display.reset();
      this.knowledgeName.reset();

      setTimeout(() => this.initKnowledgeBases(), 2000);
      setTimeout(() => this.initKnowledgeBasePolling(), 4000);
    }
  }

  onWebScrape() {
    if (this.webScrapeUrl.valid && this.knowledgeName.valid) {
      const webScrapeUrl = this.webScrapeUrl.value;
      const name = this.knowledgeName.value;
      this.isKnowledgeLoading = true;

      this.knowledgeService.uploadKnowledgeWebsite(this.aiAgent.id, webScrapeUrl, name).subscribe({
        next: () => {
        },
        error: () => {
          alert('There was an error contact support');
        }
      });

      this.openSnackBar('Webscraping request was sent successfully', 'Close');
      this.isKnowledgeLoading = false;

      this.knowledgeName.reset();
      this.webScrapeUrl.reset();

      setTimeout(() => this.initKnowledgeBases(), 2000);
      setTimeout(() => this.initKnowledgeBasePolling(), 4000);
    }
  }

  initKnowledgeBases() {
    this.knowledgeService.fetchForAiAgent(this.aiAgent.id).subscribe({
      next: (knowledgeDocuments) => {
        this.aiAgent.knowledgeDocuments = knowledgeDocuments;
        this.isKnowledgeListLoading = Array.from({ length: knowledgeDocuments.length }, () => false);
      }
    });
  }

  isWebsiteUrlKnowledgeBase(): boolean {
    return this.knowledgeBaseType.value == this.knowledgeBaseTypes[0];
  }

  removeKnowledge(knowledge: KnowledgeDocumentResponse, index: number) {
    if (knowledge.status == KnowledgeBaseStatus.PENDING) {
      alert('Knowledge base must be ready before you attempt to delete it');
      return;
    }
    
    this.isKnowledgeListLoading[index] = true;
    this.isDeleteKnowledgeLoading = true;

    this.knowledgeService.deleteDocument(knowledge.id).subscribe({
      next: () => {
        this.aiAgent.knowledgeDocuments.splice(index, 1);
        this.initKnowledgeBases();
        this.isKnowledgeListLoading[index] = false;
        this.isDeleteKnowledgeLoading = false;
        this.openSnackBar('File removed successfully', 'close');
      }
    });
  }

  // PRIVATE 

  private openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 5000
    });
  }

  private initKnowledgeBasePolling() {
    if (this.aiAgent) {
      if (this.aiAgent.knowledgeDocuments.some(k => k.status == KnowledgeBaseStatus.PENDING)) {
        this.intervalId = setInterval(() => {
          if (!this.aiAgent.knowledgeDocuments.some(k => k.status == KnowledgeBaseStatus.PENDING)) {
            clearInterval(this.intervalId);
            return;
          }
          
          this.initKnowledgeBases();
        }, 10000);
      }
    }
  }
}
